/**
 * Api — dashboard page at /dashboard/api.
 *
 * - Manage the user's personal API token (1 active per account).
 * - Show how to use the REST API at /api/v1 with copy-paste curl snippets.
 * - Offer the OpenAPI 3 spec and the LLM-friendly docs JSON as downloads.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Code, Copy, Check, AlertCircle, KeyRound, Loader2, RefreshCw,
  Trash2, Download, ExternalLink, Sparkles, ShieldCheck, Plug, Bot,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

type TokenInfo = {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
};

export function Api() {
  useEffect(() => {
    document.title = 'API · Zettapost';
  }, []);

  const qc = useQueryClient();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['api-token'],
    queryFn: () => api<{ token: TokenInfo | null }>('/api/api-tokens'),
  });
  const token = data?.token ?? null;

  const issue = useMutation({
    mutationFn: () =>
      api<{ token: string; info: TokenInfo }>('/api/api-tokens', {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (res) => {
      setRevealed(res.token);
      qc.setQueryData(['api-token'], { token: res.info });
      setConfirmReplace(false);
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err instanceof ApiError ? err.message : 'No se pudo generar el token.');
    },
  });

  const revoke = useMutation({
    mutationFn: () => api('/api/api-tokens', { method: 'DELETE' }),
    onSuccess: () => {
      qc.setQueryData(['api-token'], { token: null });
      setRevealed(null);
      setConfirmRevoke(false);
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err instanceof ApiError ? err.message : 'No se pudo revocar el token.');
    },
  });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Code size={22} className="text-brand-600" /> API y MCP
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Publica y mide tus redes desde tu propio código (API REST) o desde tu IA
          favorita (ChatGPT, Claude…) con el conector MCP. Incluido en todos los planes activos.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          {/* Token card */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                <KeyRound size={18} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Tu token de API</h2>
                <p className="text-xs text-slate-500">
                  1 token activo por cuenta. Generar uno nuevo invalida el anterior.
                </p>
              </div>
            </div>

            {isLoading ? (
              <div className="py-8 text-center text-slate-400">
                <Loader2 size={18} className="animate-spin inline" />
              </div>
            ) : revealed ? (
              <RevealedToken token={revealed} onDismiss={() => setRevealed(null)} />
            ) : token ? (
              <TokenExistsBlock
                token={token}
                onReplace={() => setConfirmReplace(true)}
                onRevoke={() => setConfirmRevoke(true)}
              />
            ) : (
              <div>
                <p className="text-sm text-slate-600 mb-4">
                  Aún no has generado ningún token.
                </p>
                <button
                  type="button"
                  onClick={() => issue.mutate()}
                  disabled={issue.isPending}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {issue.isPending ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                  Generar token
                </button>
              </div>
            )}

            {actionError && (
              <p className="mt-3 text-xs text-rose-600 inline-flex items-center gap-1">
                <AlertCircle size={11} /> {actionError}
              </p>
            )}

            {/* confirm dialogs (inline, simple) */}
            {confirmReplace && !revealed && (
              <ConfirmInline
                title="Generar un token nuevo"
                body="Esto invalidará inmediatamente tu token actual. Cualquier integración que lo use dejará de funcionar hasta que copies el nuevo."
                confirmLabel="Sí, regenerar"
                onConfirm={() => issue.mutate()}
                onCancel={() => setConfirmReplace(false)}
                pending={issue.isPending}
                tone="warn"
              />
            )}
            {confirmRevoke && (
              <ConfirmInline
                title="Revocar el token"
                body="Tu token actual dejará de funcionar inmediatamente. Podrás generar uno nuevo cuando quieras."
                confirmLabel="Sí, revocar"
                onConfirm={() => revoke.mutate()}
                onCancel={() => setConfirmRevoke(false)}
                pending={revoke.isPending}
                tone="danger"
              />
            )}
          </section>

          <McpSection />

          <DocsBlock />
        </div>

        <aside className="space-y-4">
          <DownloadCard />
          <SafetyCard />
        </aside>
      </div>
    </>
  );
}

/* ── Sub-components ───────────────────────────────────── */

/* ── MCP connector section ──────────────────────────────── */
function McpSection() {
  const [copied, setCopied] = useState(false);
  const mcpUrl = `${window.location.origin}/mcp`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  const tools = [
    'Publicar ahora',
    'Programar publicaciones',
    'Ver analíticas',
    'Listar cuentas',
    'Estado de un post',
  ];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
          <Plug size={18} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">Conecta tu IA (MCP)</h2>
          <p className="text-xs text-slate-500">
            Usa Zettapost desde ChatGPT, Claude, Cursor y otros clientes compatibles con MCP.
          </p>
        </div>
      </div>

      <p className="text-sm text-slate-600 mb-3">
        El conector MCP te deja publicar, programar y consultar tus métricas hablando con tu IA.
        Inicias sesión con tu cuenta de Zettapost (OAuth) — <strong>no necesitas pegar ningún token</strong>.
      </p>

      {/* Connection URL */}
      <label className="text-xs font-medium text-slate-500">URL del conector</label>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 text-sm font-mono text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 truncate">
          {mcpUrl}
        </code>
        <button type="button" onClick={copy} className="btn-secondary gap-2 shrink-0">
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>

      {/* Steps */}
      <ol className="mt-4 space-y-2 text-sm text-slate-600">
        {[
          <>En tu IA, añade un <strong>conector / servidor MCP remoto</strong> (en ChatGPT: Configuración → Conectores; en Claude: Settings → Connectors).</>,
          <>Pega la <strong>URL del conector</strong> de arriba.</>,
          <>Se abrirá el login de Zettapost: <strong>inicia sesión y autoriza</strong> el acceso.</>,
          <>¡Listo! Pídele cosas como «programa este post en Instagram para el viernes a las 10».</>,
        ].map((step, i) => (
          <li key={i} className="flex gap-2.5">
            <span className="shrink-0 h-5 w-5 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      {/* Tools */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-xs font-medium text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Bot size={13} /> Lo que tu IA podrá hacer
        </div>
        <div className="flex flex-wrap gap-1.5">
          {tools.map((t) => (
            <span key={t} className="text-xs bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 text-slate-600">{t}</span>
          ))}
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-400">
        El conector respeta tu plan y tus cuentas conectadas. Puedes revocar el acceso en cualquier momento desde tu cliente de IA.
      </p>
    </section>
  );
}

function RevealedToken({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      /* ignore */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }
  return (
    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
      <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
        <Sparkles size={14} /> Tu token nuevo
      </p>
      <p className="text-xs text-emerald-700/90 mt-1">
        <strong>Guárdalo ahora.</strong> Por seguridad no podremos volver a mostrártelo —
        si lo pierdes, tendrás que generar otro.
      </p>
      <div className="mt-3 flex gap-2">
        <code className="flex-1 px-3 py-2 rounded-lg bg-white border border-emerald-200 text-xs font-mono text-slate-900 break-all">
          {token}
        </code>
        <button
          type="button"
          onClick={copy}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition',
            copied
              ? 'bg-emerald-600 text-white'
              : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100',
          )}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-3 text-xs text-emerald-700 hover:text-emerald-900 underline"
      >
        Lo he guardado, ocultar
      </button>
    </div>
  );
}

function TokenExistsBlock({
  token,
  onReplace,
  onRevoke,
}: {
  token: TokenInfo;
  onReplace: () => void;
  onRevoke: () => void;
}) {
  return (
    <div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
        <div className="min-w-0">
          <code className="text-sm font-mono text-slate-700 truncate block">
            {token.prefix}… (oculto)
          </code>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Creado {new Date(token.createdAt).toLocaleDateString('es-ES')}
            {token.lastUsedAt
              ? ` · último uso ${new Date(token.lastUsedAt).toLocaleString('es-ES')}`
              : ' · sin uso aún'}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onReplace}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition"
        >
          <RefreshCw size={12} /> Regenerar
        </button>
        <button
          type="button"
          onClick={onRevoke}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-200 transition"
        >
          <Trash2 size={12} /> Revocar
        </button>
      </div>
    </div>
  );
}

function ConfirmInline({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  pending,
  tone,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  pending: boolean;
  tone: 'warn' | 'danger';
}) {
  return (
    <div
      className={cn(
        'mt-4 rounded-lg p-3 border',
        tone === 'warn'
          ? 'bg-amber-50 border-amber-200 text-amber-900'
          : 'bg-rose-50 border-rose-200 text-rose-900',
      )}
    >
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-xs mt-1 opacity-90">{body}</p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          disabled={pending}
          onClick={onConfirm}
          className={cn(
            'px-3 py-1.5 rounded-md text-xs font-semibold',
            tone === 'warn'
              ? 'bg-amber-600 text-white hover:bg-amber-700'
              : 'bg-rose-600 text-white hover:bg-rose-700',
            'disabled:opacity-50',
          )}
        >
          {pending && <Loader2 size={11} className="animate-spin inline mr-1" />}
          {confirmLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function DownloadCard() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
        <Download size={14} /> Especificación descargable
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        Pásale uno de estos JSON a una IA o tu cliente HTTP favorito.
      </p>
      <ul className="space-y-1.5 text-xs">
        <li>
          <a
            href="/api/v1/openapi.json"
            download
            className="inline-flex items-center gap-1 text-brand-600 hover:underline"
          >
            <Download size={11} /> openapi.json (OpenAPI 3.0)
          </a>
        </li>
        <li>
          <a
            href="/api/v1/docs.json"
            download
            className="inline-flex items-center gap-1 text-brand-600 hover:underline"
          >
            <Download size={11} /> docs.json (formato amigable para IA)
          </a>
        </li>
      </ul>
    </div>
  );
}

function SafetyCard() {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
        <ShieldCheck size={14} /> Buenas prácticas
      </h3>
      <ul className="text-xs text-amber-900/90 space-y-1.5 list-disc pl-4">
        <li>Trata el token como una contraseña; no lo subas a Git ni a código cliente.</li>
        <li>Guárdalo en variables de entorno o en un secret manager.</li>
        <li>Si crees que se ha filtrado, pulsa "Regenerar".</li>
        <li>El plan debe estar activo para publicar (devuelve HTTP 402 si no).</li>
        <li>Rate limit: 120 peticiones/min por token.</li>
      </ul>
    </div>
  );
}

function DocsBlock() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
            <Code size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Documentación de la API</h2>
            <p className="text-xs text-slate-500">Endpoints REST para integrarlo en tu propio código.</p>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Base URL: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">https://zettapost.com/api/v1</code>
          {' · '}
          Auth: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">Authorization: Bearer &lt;token&gt;</code>
        </p>
      </div>

      <EndpointBlock
        method="GET"
        path="/me"
        description="Devuelve tu usuario, el plan activo y si tienes derecho a publicar."
        curl={`curl https://zettapost.com/api/v1/me \\
  -H "Authorization: Bearer zpk_live_xxx"`}
      />

      <EndpointBlock
        method="GET"
        path="/accounts"
        description="Lista las cuentas de redes sociales conectadas. Cada item incluye accountId, platform, username y connected:boolean."
        curl={`curl https://zettapost.com/api/v1/accounts \\
  -H "Authorization: Bearer zpk_live_xxx"`}
        response={`{
  "accounts": [
    { "accountId": "uuid", "platform": "instagram", "username": "@yo", "connected": true },
    { "accountId": "uuid", "platform": "x",          "username": "@yo", "connected": true }
  ]
}`}
      />

      <EndpointBlock
        method="POST"
        path="/posts"
        description="Crea un post. Si omites scheduledAt se publica de inmediato (entra en cola)."
        curl={`curl -X POST https://zettapost.com/api/v1/posts \\
  -H "Authorization: Bearer zpk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "accountId": "<UUID de /accounts>",
    "caption": "Hola mundo 👋",
    "kind": "post",
    "media": [
      { "url": "https://picsum.photos/1080" }
    ]
  }'`}
        response={`{
  "postId": "uuid",
  "scheduledAt": "2026-05-25T10:00:00.000Z",
  "status": "scheduled",
  "targets": [{ "accountId": "uuid", "status": "scheduled" }]
}`}
      />

      <EndpointBlock
        method="GET"
        path="/posts/{id}"
        description="Estado por cuenta destino: scheduled / publishing / published / failed. El campo error explica el motivo si falla."
        curl={`curl https://zettapost.com/api/v1/posts/<POST_ID> \\
  -H "Authorization: Bearer zpk_live_xxx"`}
      />

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Errores frecuentes</h3>
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="text-left">
              <th className="font-medium py-1">HTTP</th>
              <th className="font-medium py-1">error</th>
              <th className="font-medium py-1">qué hacer</th>
            </tr>
          </thead>
          <tbody className="text-slate-700">
            <tr className="border-t border-slate-100"><td className="py-1.5">401</td><td>AUTH_REQUIRED / INVALID_TOKEN</td><td>Genera o copia bien el token.</td></tr>
            <tr className="border-t border-slate-100"><td className="py-1.5">402</td><td>PLAN_REQUIRED</td><td>Tu plan está inactivo — reanúdalo en /dashboard/billing.</td></tr>
            <tr className="border-t border-slate-100"><td className="py-1.5">400</td><td>ACCOUNT_NOT_CONNECTED</td><td>Reconecta la cuenta en /dashboard/accounts.</td></tr>
            <tr className="border-t border-slate-100"><td className="py-1.5">400</td><td>MEDIA_FETCH_FAILED</td><td>Comprueba que la URL del media es pública y devuelve 200.</td></tr>
            <tr className="border-t border-slate-100"><td className="py-1.5">400</td><td>INVALID_MEDIA_TYPE</td><td>Solo aceptamos image/jpeg|png|webp|gif y video/mp4|quicktime|webm.</td></tr>
            <tr className="border-t border-slate-100"><td className="py-1.5">429</td><td>RATE_LIMITED</td><td>Has superado 120 req/min; espera un poco o agrupa llamadas.</td></tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EndpointBlock({
  method,
  path,
  description,
  curl,
  response,
}: {
  method: string;
  path: string;
  description: string;
  curl: string;
  response?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span
          className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
            method === 'GET' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700',
          )}
        >
          {method}
        </span>
        <code className="text-sm font-mono text-slate-900">{path}</code>
      </div>
      <p className="text-xs text-slate-600 mb-2">{description}</p>
      <CodeBlock code={curl} />
      {response && (
        <>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-2 mb-1">Respuesta</p>
          <CodeBlock code={response} />
        </>
      )}
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(code); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 text-[11px] leading-relaxed p-3 rounded-lg overflow-x-auto font-mono">
        {code}
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 px-2 py-1 rounded text-[10px] font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition"
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
  );
}

// Silence the unused-import warning while keeping ExternalLink available.
void ExternalLink;
